/**
 * FR-110…116 · Cài đặt hệ thống — bám bố cục `settings.html`:
 *
 *   page-head (liên kết sang Kết nối Cloudflare · Khôi phục mặc định · Lưu thay đổi)
 *   card[data-tabs] > .tabs + .tabpanel > .card-body > .grid.g-2 > card lồng
 *
 * Prototype có 4 tab, tài liệu (docs/08 §8.1, FR-110…114) yêu cầu 5 → theo tài
 * liệu, giữ lại `data-od-id` cũ ở chỗ khớp nghĩa (OQ-014). Chỉ dựng những khoá
 * thật sự có trong `DEFAULT_SETTINGS` (OQ-015).
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_SETTINGS, FORCE_2FA_ALL, SECRET_SETTING_KEYS } from '@dnshub/shared';
import { api, ApiError, thanhApiError, useLoiApi } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { useFormat } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { Field } from '../../components/Field';
import { AsyncSection, SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';
import {
  SettingList,
  SettingNumber,
  SettingSelect,
  SettingSwitch,
  SettingText,
} from './SettingsControls';
import type { SettingsPayload, UpdateSettingsResult } from './types';
import { BackupPanel } from './BackupPanel';
import { BackupSchedulePanel } from './BackupSchedulePanel';
import { RemoteBackupPanel } from './RemoteBackupPanel';
import { DomainAddPolicyCard } from './DomainAddPolicyCard';
import { MailTemplatePanel } from './MailTemplatePanel';
import { AuditArchivePanel } from './AuditArchivePanel';

const TABS = ['security', 'sync', 'policy', 'smtp', 'mail', 'branding', 'backup'] as const;
type Tab = (typeof TABS)[number];

/** `data-od-id` giữ nguyên của prototype; `panel-sync` là tab mới (OQ-014). */
const PANEL_OD_ID: Record<Tab, string> = {
  security: 'panel-security',
  sync: 'panel-sync',
  policy: 'panel-policy',
  smtp: 'panel-notify',
  mail: 'panel-mail',
  branding: 'panel-brand',
  backup: 'panel-backup',
};

type Values = Record<string, unknown>;

export function SettingsPage() {
  const t = useT();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = TABS.includes(raw as Tab) ? (raw as Tab) : 'security';

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingsPayload>('/settings'),
  });

  const [draft, setDraft] = useState<Values>({});
  const [showReset, setShowReset] = useState(false);
  const [showSmtpTest, setShowSmtpTest] = useState(false);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  useEffect(() => {
    if (settingsQuery.data) setDraft(settingsQuery.data.values);
  }, [settingsQuery.data]);

  const saved = settingsQuery.data?.values ?? {};
  const changed = useMemo(
    () =>
      Object.keys(draft).filter((key) => JSON.stringify(draft[key]) !== JSON.stringify(saved[key])),
    [draft, saved],
  );

  const set = (key: string, value: unknown) => setDraft((prev) => ({ ...prev, [key]: value }));
  const num = (key: string) => Number(draft[key] ?? DEFAULT_SETTINGS[key] ?? 0);
  const bool = (key: string) => Boolean(draft[key] ?? DEFAULT_SETTINGS[key]);
  const text = (key: string) => String(draft[key] ?? DEFAULT_SETTINGS[key] ?? '');
  const list = (key: string) => (Array.isArray(draft[key]) ? (draft[key] as string[]) : []);

  const onError = (err: unknown) => {
    setError(thanhApiError(err));
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));
  };

  const save = useMutation({
    mutationFn: (patch: Values) => api.patch<UpdateSettingsResult>('/settings', patch),
    onSuccess: async (result) => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast(t('settings.saved', { count: result.changed.length }));
    },
    onError,
  });

  const syncAll = useMutation({
    mutationFn: () => api.post<{ jobId: string }>('/settings/sync-all'),
    onSuccess: () => toast(t('settings.sync.queued')),
    onError,
  });

  const saveChanged = () => {
    if (changed.length === 0) return;
    const patch: Values = {};
    for (const key of changed) patch[key] = draft[key];
    save.mutate(patch);
  };

  const resetGroup = () => {
    const patch: Values = {};
    for (const key of Object.keys(DEFAULT_SETTINGS)) {
      if (!key.startsWith(`${tab}.`)) continue;
      /*
       * OQ-240 — dùng danh sách chung thay vì tên cứng. Bản cũ viết thẳng
       * `'smtp.password'`, nên khoá bí mật thứ hai (`smtp.resend_api_key`) sẽ
       * bị "Khôi phục mặc định" xoá mất mà không ai ngờ — API trả về `***`
       * cho khoá bí mật, nên gán lại giá trị mặc định `''` là xoá thật.
       */
      if ((SECRET_SETTING_KEYS as readonly string[]).includes(key)) continue;
      patch[key] = DEFAULT_SETTINGS[key];
    }
    save.mutate(patch, { onSuccess: () => setShowReset(false) });
  };

  return (
    <div data-od-id="page-settings">
      <div className="page-head">
        <div className="grow">
          <h1 data-od-id="settings-title">{t('settings.title')}</h1>
          <p className="lede">
            {t('settings.lede')} <Link to="/connections">{t('settings.cloudflareLink')}</Link>.
          </p>
        </div>
        <div className="row">
          <Link className="btn" to="/connections">
            {t('settings.cloudflareLink')}
          </Link>
          <button className="btn" type="button" onClick={() => setShowReset(true)}>
            {t('settings.restoreDefaults')}
          </button>
          <button
            className="btn btn-primary"
            type="button"
            data-od-id="settings-save"
            disabled={changed.length === 0 || save.isPending}
            onClick={saveChanged}
          >
            {save.isPending ? t('common.processing') : t('common.saveChanges')}
          </button>
        </div>
      </div>

      <AsyncSection
        loading={settingsQuery.isLoading}
        error={settingsQuery.error}
        data={settingsQuery.data}
        onRetry={() => void settingsQuery.refetch()}
        skeleton={<SkeletonCard rows={8} />}
      >
        {() => (
          <div className="card" data-od-id="settings-tabs">
            <div className="tabs" role="tablist">
              {TABS.map((name) => (
                <button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={tab === name}
                  onClick={() => setParams({ tab: name }, { replace: true })}
                >
                  {t(`settings.tab.${name}`)}
                </button>
              ))}
            </div>

            {error && error.fields.length > 0 ? (
              <div className="card-body" style={{ paddingBottom: 0 }}>
                <div className="form-alert bad" role="alert">
                  <Icon name="warn" size={16} />
                  <span>{error.fields.map((field) => field.message).join(' · ')}</span>
                </div>
              </div>
            ) : null}

            {tab === 'security' ? (
              <div className="tabpanel" role="tabpanel" data-od-id={PANEL_OD_ID.security}>
                {/* `.stack` thay cho `.mt-20` vá tay trên từng thẻ — cùng cách
                    với các tab khác, xem OQ-318. */}
                <div className="card-body stack">
                  <div className="grid g-2">
                    <div className="card">
                      <div className="card-head">
                        <h2>{t('settings.security.auth')}</h2>
                      </div>
                      <div className="card-body">
                        <SettingSwitch
                          label={t('setting.security.force_2fa_all')}
                          sub={t('setting.security.force_2fa_all.hint')}
                          checked={list('security.force_2fa_roles').includes(FORCE_2FA_ALL)}
                          onChange={(next) => {
                            const roles = list('security.force_2fa_roles').filter(
                              (slug) => slug !== FORCE_2FA_ALL,
                            );
                            set(
                              'security.force_2fa_roles',
                              next ? [FORCE_2FA_ALL, ...roles] : roles,
                            );
                          }}
                        />
                        <SettingList
                          label={t('setting.security.force_2fa_roles')}
                          hint={t('setting.security.force_2fa_roles.hint')}
                          value={list('security.force_2fa_roles').filter(
                            (slug) => slug !== FORCE_2FA_ALL,
                          )}
                          onChange={(next) =>
                            set(
                              'security.force_2fa_roles',
                              list('security.force_2fa_roles').includes(FORCE_2FA_ALL)
                                ? [FORCE_2FA_ALL, ...next]
                                : next,
                            )
                          }
                        />
                        <SettingNumber
                          label={t('setting.security.session_absolute_days')}
                          sub={t('setting.security.session_absolute_days.hint')}
                          value={num('security.session_absolute_days')}
                          min={1}
                          max={365}
                          onChange={(next) => set('security.session_absolute_days', next)}
                        />
                        <SettingNumber
                          label={t('setting.security.session_idle_days')}
                          sub={t('setting.security.session_idle_days.hint')}
                          value={num('security.session_idle_days')}
                          min={1}
                          max={365}
                          onChange={(next) => set('security.session_idle_days', next)}
                        />
                        <SettingSwitch
                          label={t('setting.security.allow_multiple_sessions')}
                          sub={t('setting.security.allow_multiple_sessions.hint')}
                          checked={bool('security.allow_multiple_sessions')}
                          onChange={(next) => set('security.allow_multiple_sessions', next)}
                        />
                        <SettingNumber
                          label={t('setting.security.trusted_device_days')}
                          sub={t('setting.security.trusted_device_days.hint')}
                          value={num('security.trusted_device_days')}
                          min={1}
                          max={365}
                          onChange={(next) => set('security.trusted_device_days', next)}
                        />
                        <SettingSwitch
                          label={t('setting.security.geoip_enabled')}
                          sub={t('setting.security.geoip_enabled.hint')}
                          checked={bool('security.geoip_enabled')}
                          onChange={(next) => set('security.geoip_enabled', next)}
                        />
                      </div>
                    </div>

                    <div className="card">
                      <div className="card-head">
                        <h2>{t('settings.security.access')}</h2>
                      </div>
                      <div className="card-body">
                        <SettingList
                          label={t('setting.security.admin_ip_allowlist')}
                          hint={t('setting.security.admin_ip_allowlist.hint')}
                          value={list('security.admin_ip_allowlist')}
                          onChange={(next) => set('security.admin_ip_allowlist', next)}
                        />
                        <SettingNumber
                          label={t('setting.security.max_failed_logins')}
                          sub={t('setting.security.max_failed_logins.hint')}
                          value={num('security.max_failed_logins')}
                          min={1}
                          max={20}
                          onChange={(next) => set('security.max_failed_logins', next)}
                        />
                        <SettingNumber
                          label={t('setting.security.lockout_minutes')}
                          sub={t('setting.security.lockout_minutes.hint')}
                          value={num('security.lockout_minutes')}
                          min={1}
                          max={1440}
                          onChange={(next) => set('security.lockout_minutes', next)}
                        />
                        <SettingNumber
                          label={t('setting.security.invitation_expiry_days')}
                          sub={t('setting.security.invitation_expiry_days.hint')}
                          value={num('security.invitation_expiry_days')}
                          min={1}
                          max={90}
                          onChange={(next) => set('security.invitation_expiry_days', next)}
                        />
                        <SettingNumber
                          label={t('setting.security.password_reset_ttl_minutes')}
                          sub={t('setting.security.password_reset_ttl_minutes.hint')}
                          value={num('security.password_reset_ttl_minutes')}
                          min={5}
                          max={1440}
                          onChange={(next) => set('security.password_reset_ttl_minutes', next)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-head">
                      <h2>{t('settings.security.password')}</h2>
                    </div>
                    <div className="card-body">
                      <SettingNumber
                        label={t('setting.security.min_password_length')}
                        sub={t('setting.security.min_password_length.hint')}
                        value={num('security.min_password_length')}
                        min={8}
                        max={64}
                        onChange={(next) => set('security.min_password_length', next)}
                      />
                      <SettingSwitch
                        label={t('setting.security.require_uppercase')}
                        checked={bool('security.require_uppercase')}
                        onChange={(next) => set('security.require_uppercase', next)}
                      />
                      <SettingSwitch
                        label={t('setting.security.require_lowercase')}
                        checked={bool('security.require_lowercase')}
                        onChange={(next) => set('security.require_lowercase', next)}
                      />
                      <SettingSwitch
                        label={t('setting.security.require_number')}
                        checked={bool('security.require_number')}
                        onChange={(next) => set('security.require_number', next)}
                      />
                      <SettingSwitch
                        label={t('setting.security.require_symbol')}
                        sub={t('setting.security.require_symbol.hint')}
                        checked={bool('security.require_symbol')}
                        onChange={(next) => set('security.require_symbol', next)}
                      />
                      <SettingNumber
                        label={t('setting.security.password_history')}
                        sub={t('setting.security.password_history.hint')}
                        value={num('security.password_history')}
                        min={0}
                        max={20}
                        onChange={(next) => set('security.password_history', next)}
                      />
                      <SettingNumber
                        label={t('setting.security.password_expiry_days')}
                        sub={t('setting.security.password_expiry_days.hint')}
                        value={num('security.password_expiry_days')}
                        min={0}
                        max={365}
                        onChange={(next) => set('security.password_expiry_days', next)}
                      />
                      <SettingSwitch
                        label={t('setting.security.check_pwned_passwords')}
                        sub={t('setting.security.check_pwned_passwords.hint')}
                        checked={bool('security.check_pwned_passwords')}
                        onChange={(next) => set('security.check_pwned_passwords', next)}
                      />
                    </div>
                  </div>

                  {/*
                    OQ-315 · OQ-316 · OQ-319 — tự đăng ký và hai lớp chắn của nó.

                    Chủ dự án: *"tôi nghĩ nên chuyển nhóm cài đặt này sang tab bảo
                    mật thì hợp lý hơn"*. Đúng: bốn trong năm mục là khoá
                    `security.*`, và câu hỏi mà nhóm này trả lời — *ai vào được hệ
                    thống, và chặn máy gửi rác thế nào* — là câu hỏi bảo mật, không
                    phải quy tắc thêm tên miền.

                    Gom một thẻ riêng vì chúng chỉ có nghĩa khi đi cùng nhau: tắt
                    đăng ký thì captcha và giới hạn IP đều không dùng tới.
                  */}
                  <div className="card">
                    <div className="card-head">
                      <div className="grow">
                        <h2>{t('settings.signup.title')}</h2>
                        <div className="sub">{t('settings.signup.sub')}</div>
                      </div>
                    </div>
                    <div className="card-body">
                      <SettingSwitch
                        label={t('setting.policy.allow_public_signup')}
                        sub={t('setting.policy.allow_public_signup.hint')}
                        checked={bool('policy.allow_public_signup')}
                        onChange={(next) => set('policy.allow_public_signup', next)}
                      />
                      <SettingNumber
                        label={t('setting.security.signup_rate_limit')}
                        sub={t('setting.security.signup_rate_limit.hint')}
                        value={num('security.signup_rate_limit')}
                        min={1}
                        max={1000}
                        onChange={(next) => set('security.signup_rate_limit', next)}
                      />
                      <SettingNumber
                        label={t('setting.security.signup_rate_window_minutes')}
                        sub={t('setting.security.signup_rate_window_minutes.hint')}
                        value={num('security.signup_rate_window_minutes')}
                        min={1}
                        max={10080}
                        onChange={(next) => set('security.signup_rate_window_minutes', next)}
                      />
                      <SettingSwitch
                        label={t('setting.security.turnstile_enabled')}
                        sub={t('setting.security.turnstile_enabled.hint')}
                        checked={bool('security.turnstile_enabled')}
                        onChange={(next) => set('security.turnstile_enabled', next)}
                      />
                      {bool('security.turnstile_enabled') ? (
                        <>
                          <SettingText
                            label={t('setting.security.turnstile_site_key')}
                            hint={t('setting.security.turnstile_site_key.hint')}
                            value={text('security.turnstile_site_key')}
                            onChange={(next) => set('security.turnstile_site_key', next)}
                          />
                          <SettingText
                            label={t('setting.security.turnstile_secret_key')}
                            type="password"
                            hint={t('setting.security.turnstile_secret_key.hint')}
                            value={text('security.turnstile_secret_key')}
                            onChange={(next) => set('security.turnstile_secret_key', next)}
                          />
                          {/* Thiếu một trong hai khoá thì máy chủ coi như captcha
                              TẮT (`turnstileDangBaoVe`). Nói ra ở đây, chứ đừng để
                              người vận hành tin rằng mình đang được bảo vệ. */}
                          {!text('security.turnstile_site_key') ||
                          !text('security.turnstile_secret_key') ? (
                            <div className="form-alert warn" role="alert">
                              <span>{t('setting.security.turnstile_missing_keys')}</span>
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === 'sync' ? (
              <div className="tabpanel" role="tabpanel" data-od-id={PANEL_OD_ID.sync}>
                {/* Hai thẻ con (cấu hình + `SyncJobsCard`) — cùng cảnh với tab
                    Chính sách và Sao lưu, xem OQ-318. */}
                <div className="card-body stack">
                  <div className="card">
                    <div className="card-head">
                      <div className="grow">
                        <h2>{t('settings.sync.title')}</h2>
                        <div className="sub">{t('settings.sync.sub')}</div>
                      </div>
                    </div>
                    <div className="card-body">
                      <SettingNumber
                        label={t('setting.sync.zone_interval_minutes')}
                        sub={t('setting.sync.zone_interval_minutes.hint')}
                        value={num('sync.zone_interval_minutes')}
                        min={5}
                        max={10080}
                        onChange={(next) => set('sync.zone_interval_minutes', next)}
                      />
                      <SettingNumber
                        label={t('setting.sync.token_check_interval_minutes')}
                        sub={t('setting.sync.token_check_interval_minutes.hint')}
                        value={num('sync.token_check_interval_minutes')}
                        min={5}
                        max={10080}
                        onChange={(next) => set('sync.token_check_interval_minutes', next)}
                      />
                      {/*
                        OQ-289 — hai ô này thiếu từ M7 dù cả hai khoá đều được
                        đọc thật trong `jobs.ts`. Cùng loại thiếu sót với lịch
                        sao lưu (OQ-286): tính năng chạy đúng mà không ai điều
                        khiển được.
                      */}
                      <SettingNumber
                        label={t('setting.sync.verify_ns_interval_minutes')}
                        sub={t('setting.sync.verify_ns_interval_minutes.hint')}
                        value={num('sync.verify_ns_interval_minutes')}
                        min={5}
                        max={10080}
                        onChange={(next) => set('sync.verify_ns_interval_minutes', next)}
                      />
                      <SettingNumber
                        label={t('setting.sync.dns_sync_interval_minutes')}
                        sub={t('setting.sync.dns_sync_interval_minutes.hint')}
                        value={num('sync.dns_sync_interval_minutes')}
                        min={5}
                        max={10080}
                        onChange={(next) => set('sync.dns_sync_interval_minutes', next)}
                      />
                      <SettingNumber
                        label={t('setting.sync.max_retries')}
                        sub={t('setting.sync.max_retries.hint')}
                        value={num('sync.max_retries')}
                        min={0}
                        max={10}
                        onChange={(next) => set('sync.max_retries', next)}
                      />
                      <SettingNumber
                        label={t('setting.sync.backoff_base_ms')}
                        sub={t('setting.sync.backoff_base_ms.hint')}
                        value={num('sync.backoff_base_ms')}
                        min={100}
                        max={60000}
                        width={130}
                        onChange={(next) => set('sync.backoff_base_ms', next)}
                      />
                    </div>
                    <div className="card-foot">
                      <span className="small muted">{t('settings.sync.note')}</span>
                      <span style={{ flex: 1 }} />
                      <button
                        className="btn btn-sm"
                        type="button"
                        disabled={syncAll.isPending}
                        onClick={() => syncAll.mutate()}
                      >
                        {syncAll.isPending ? t('common.processing') : t('settings.sync.runNow')}
                      </button>
                    </div>
                  </div>

                  {/* docs/09 §9.4 — trang theo dõi trạng thái job cho Admin.
                      Đặt trong tab *Đồng bộ* thay vì thêm mục sidebar mới:
                      FR-001 liệt kê đóng ba nhóm sidebar. Xem OQ-075. */}
                  <SyncJobsCard />
                </div>
              </div>
            ) : null}

            {tab === 'policy' ? (
              <div className="tabpanel" role="tabpanel" data-od-id={PANEL_OD_ID.policy}>
                {/*
                  `.card-body` chỉ có `padding`, không có `gap` — ba thẻ xếp
                  thẳng trong nó sẽ dính sát nhau thành một khối liền, không còn
                  nhìn ra đâu là ranh giới giữa hai nhóm cài đặt khác nhau.

                  Ba tab còn lại đều bọc thẻ con bằng `.grid`; tab này bị bỏ sót
                  vì hồi đầu nó chỉ có một thẻ, và một thẻ thì không dính vào
                  đâu cả. Dùng `.stack` (grid một cột, gap 14px) để giữ nguyên bố
                  cục dọc, chỉ trả lại khoảng cách.
                */}
                <div className="card-body stack">
                  {/* FR-112 mục 1 — chỉ đọc, không có khoá `settings` phía sau.
                      Xem OQ-037 (quyết định) và OQ-140 (ràng buộc quyền). */}
                  <DomainAddPolicyCard />

                  <div className="card">
                    <div className="card-head">
                      <div className="grow">
                        <h2>{t('settings.policy.title')}</h2>
                        <div className="sub">{t('settings.policy.sub')}</div>
                      </div>
                    </div>
                    <div className="card-body">
                      <SettingSwitch
                        label={t('setting.policy.require_admin_approval_for_domains')}
                        sub={t('setting.policy.require_admin_approval_for_domains.hint')}
                        checked={bool('policy.require_admin_approval_for_domains')}
                        onChange={(next) => set('policy.require_admin_approval_for_domains', next)}
                      />
                      <SettingNumber
                        label={t('setting.policy.max_domains_per_user')}
                        sub={t('setting.policy.max_domains_per_user.hint')}
                        value={num('policy.max_domains_per_user')}
                        min={0}
                        max={10000}
                        onChange={(next) => set('policy.max_domains_per_user', next)}
                      />
                      <SettingNumber
                        label={t('setting.policy.max_connections_per_user')}
                        sub={t('setting.policy.max_connections_per_user.hint')}
                        value={num('policy.max_connections_per_user')}
                        min={0}
                        max={1000}
                        onChange={(next) => set('policy.max_connections_per_user', next)}
                      />
                      <SettingSwitch
                        label={t('setting.policy.allow_user_invites')}
                        sub={t('setting.policy.allow_user_invites.hint')}
                        checked={bool('policy.allow_user_invites')}
                        onChange={(next) => set('policy.allow_user_invites', next)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === 'smtp' ? (
              <div className="tabpanel" role="tabpanel" data-od-id={PANEL_OD_ID.smtp}>
                <div className="card-body">
                  <div className="grid g-2">
                    <div className="card">
                      <div className="card-head">
                        <h2>{t('settings.smtp.title')}</h2>
                      </div>
                      <div className="card-body stack">
                        {/*
                          OQ-240 — chọn đường gửi. Đặt TRƯỚC mọi ô cấu hình để
                          người dùng biết ngay ô nào cần điền, thay vì điền hết
                          rồi mới phát hiện nửa số ô không dùng đến.
                        */}
                        <div className="field">
                          <label htmlFor="mail-provider">{t('setting.smtp.provider')}</label>
                          <select
                            id="mail-provider"
                            value={text('smtp.provider') || 'smtp'}
                            onChange={(event) => set('smtp.provider', event.target.value)}
                            data-od-id="setting-mail-provider"
                          >
                            <option value="smtp">SMTP</option>
                            <option value="resend">Resend (HTTPS)</option>
                          </select>
                          <span className="hint">{t('setting.smtp.provider.hint')}</span>
                        </div>

                        {(text('smtp.provider') || 'smtp') === 'resend' ? (
                          <SettingText
                            label={t('setting.smtp.resendKey')}
                            hint={t('setting.smtp.resendKey.hint')}
                            value={text('smtp.resend_api_key')}
                            mono
                            placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxx"
                            onChange={(next) => set('smtp.resend_api_key', next)}
                          />
                        ) : (
                          <>
                            <div className="grid g-2">
                              <SettingText
                                label={t('setting.smtp.host')}
                                value={text('smtp.host')}
                                mono
                                placeholder="smtp.vnhost.vn"
                                onChange={(next) => set('smtp.host', next)}
                              />
                              <div className="field">
                                <label htmlFor="smtp-port">{t('setting.smtp.port')}</label>
                                <input
                                  id="smtp-port"
                                  type="number"
                                  min={1}
                                  max={65535}
                                  value={num('smtp.port')}
                                  onChange={(event) => {
                                    /*
                                     * OQ-242 — đổi cổng thì đặt luôn TLS theo quy ước.
                                     *
                                     * Ghép sai hai giá trị này là lỗi cấu hình phổ
                                     * biến nhất, và nó ném ra thứ không ai đoán nổi
                                     * ("wrong version number", hoặc treo tới timeout).
                                     * Quy ước là chuẩn chung: 465 dùng TLS ngay từ
                                     * đầu, mọi cổng khác dùng STARTTLS.
                                     *
                                     * Vẫn để công tắc sửa được sau — máy chủ lạ có
                                     * thể lệch quy ước, và đoán hộ rồi khoá lại thì
                                     * người dùng không có đường ra.
                                     */
                                    const cong = Number(event.target.value);
                                    set('smtp.port', cong);
                                    set('smtp.secure', cong === 465);
                                  }}
                                />
                              </div>
                            </div>
                            <SettingText
                              label={t('setting.smtp.user')}
                              value={text('smtp.user')}
                              onChange={(next) => set('smtp.user', next)}
                            />
                            <SettingText
                              label={t('setting.smtp.password')}
                              type="password"
                              hint={t('setting.smtp.password.hint')}
                              value={text('smtp.password')}
                              onChange={(next) => set('smtp.password', next)}
                            />
                            <SettingSwitch
                              label={t('setting.smtp.secure')}
                              sub={t('setting.smtp.secure.hint')}
                              checked={bool('smtp.secure')}
                              onChange={(next) => set('smtp.secure', next)}
                            />
                            {/*
                              OQ-242 — cảnh báo khi cặp cổng/TLS lệch quy ước.
                              Cần cho cấu hình ĐÃ LƯU sai từ trước: người dùng
                              không đụng vào ô cổng nên nhánh tự-đặt không chạy,
                              và họ chỉ biết sai khi gặp "wrong version number".
                            */}
                            {(num('smtp.port') === 465) !== bool('smtp.secure') ? (
                              <div className="form-alert warn" role="alert">
                                <Icon name="warn" size={16} />
                                <span>
                                  {t(
                                    num('smtp.port') === 465
                                      ? 'setting.smtp.tlsMismatch465'
                                      : 'setting.smtp.tlsMismatchStarttls',
                                    { port: String(num('smtp.port')) },
                                  )}
                                </span>
                              </div>
                            ) : null}
                          </>
                        )}
                        <button
                          className="btn btn-sm"
                          type="button"
                          style={{ justifySelf: 'start' }}
                          onClick={() => setShowSmtpTest(true)}
                        >
                          {t('settings.smtp.test')}
                        </button>
                      </div>
                    </div>

                    <div className="card">
                      <div className="card-head">
                        <h2>{t('settings.smtp.sender')}</h2>
                      </div>
                      <div className="card-body stack">
                        <SettingText
                          label={t('setting.smtp.from_email')}
                          type="email"
                          value={text('smtp.from_email')}
                          onChange={(next) => set('smtp.from_email', next)}
                        />
                        <SettingText
                          label={t('setting.smtp.from_name')}
                          value={text('smtp.from_name')}
                          onChange={(next) => set('smtp.from_name', next)}
                        />
                        <div className="scope-note">
                          <Icon name="info" size={17} />
                          <span>{t('settings.smtp.eventsNote')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === 'mail' ? (
              <div className="tabpanel" role="tabpanel" data-od-id={PANEL_OD_ID.mail}>
                <MailTemplatePanel />
              </div>
            ) : null}

            {tab === 'branding' ? (
              <div className="tabpanel" role="tabpanel" data-od-id={PANEL_OD_ID.branding}>
                <div className="card-body">
                  <div className="grid g-2">
                    <div className="card">
                      <div className="card-head">
                        <h2>{t('settings.branding.org')}</h2>
                      </div>
                      <div className="card-body stack">
                        <SettingText
                          label={t('setting.branding.app_name')}
                          hint={t('setting.branding.app_name.hint')}
                          value={text('branding.app_name')}
                          onChange={(next) => set('branding.app_name', next)}
                        />
                        <SettingText
                          label={t('setting.branding.tagline')}
                          hint={t('setting.branding.tagline.hint')}
                          value={text('branding.tagline')}
                          onChange={(next) => set('branding.tagline', next)}
                        />
                        <SettingText
                          label={t('setting.branding.footer_text')}
                          value={text('branding.footer_text')}
                          onChange={(next) => set('branding.footer_text', next)}
                        />
                        <SettingText
                          label={t('setting.branding.accent_color')}
                          hint={t('setting.branding.accent_color.hint')}
                          mono
                          value={text('branding.accent_color')}
                          onChange={(next) => set('branding.accent_color', next)}
                        />
                      </div>
                    </div>

                    <div className="card">
                      <div className="card-head">
                        <h2>{t('settings.branding.appearance')}</h2>
                      </div>
                      <div className="card-body">
                        <SettingSelect
                          label={t('setting.branding.default_locale')}
                          sub={t('setting.branding.default_locale.hint')}
                          value={text('branding.default_locale')}
                          onChange={(next) => set('branding.default_locale', next)}
                        >
                          <option value="vi">Tiếng Việt</option>
                          <option value="en">English</option>
                        </SettingSelect>
                        <SettingText
                          label={t('setting.branding.default_timezone')}
                          mono
                          value={text('branding.default_timezone')}
                          onChange={(next) => set('branding.default_timezone', next)}
                        />
                        <LogoRow
                          slot="logo_light"
                          label={t('setting.branding.logo_light')}
                          url={saved['branding.logo_light'] as string | null}
                        />
                        <LogoRow
                          slot="logo_dark"
                          label={t('setting.branding.logo_dark')}
                          url={saved['branding.logo_dark'] as string | null}
                        />
                        <LogoRow
                          slot="favicon"
                          label={t('setting.branding.favicon')}
                          url={saved['branding.favicon'] as string | null}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === 'backup' ? (
              /*
                Bọc trong `.card-body.stack` như năm tab còn lại (OQ-318).

                Trước đây ba panel xếp thẳng trong một Fragment: không padding,
                không gap — nên thẻ đầu dính vào hàng tab và các thẻ dính vào
                nhau.
              */
              <div className="card-body stack">
                {/*
                  OQ-286 — lịch sao lưu đứng TRƯỚC danh sách bản sao lưu.
                  Người vào tab này thường để trả lời "máy có tự sao lưu không",
                  và câu ấy phải ở chỗ mắt chạm đầu tiên.
                */}
                <BackupSchedulePanel
                  bat={bool('backup.auto_enabled')}
                  gio={num('backup.interval_hours')}
                  giu={num('backup.keep_count')}
                  onDoi={set}
                />

                <BackupPanel />
                {/*
              OQ-273 — kho ngoài đứng NGAY DƯỚI khối sao lưu tại chỗ.
              Hai khối trả lời hai câu khác nhau: "tôi có những bản nào trên máy
              chủ" và "nếu mất luôn máy chủ thì sao". Đặt cạnh nhau để câu thứ
              hai không bị quên.
            */}
                <RemoteBackupPanel caiDat={draft as Record<string, unknown>} onDoiCaiDat={set} />

                {/*
                  OQ-333 — dọn nhật ký cũ đứng cuối tab Sao lưu.

                  Nó sinh ra tệp và nằm cùng thư mục với bản sao lưu CSDL, nên
                  người đi tìm "các tệp hệ thống đã tạo ra" tìm ở một chỗ. Đặt
                  cuối vì đây là việc hiếm khi làm — không nên chắn trước hai khối
                  người ta mở tab này để xem.
                */}
                <AuditArchivePanel />
              </div>
            ) : null}

            <div className="card-foot">
              <span className="small muted">{t('settings.auditNote')}</span>
              <span style={{ flex: 1 }} />
              <span className="small muted num">
                {t('settings.changedCount', { count: changed.length })}
              </span>
              <button
                className="btn btn-sm btn-primary"
                type="button"
                disabled={changed.length === 0 || save.isPending}
                onClick={saveChanged}
              >
                {save.isPending ? t('common.processing') : t('common.saveChanges')}
              </button>
            </div>
          </div>
        )}
      </AsyncSection>

      {showReset ? (
        <Modal
          open
          title={t('settings.reset.title')}
          subtitle={t('settings.reset.subtitle', { tab: t(`settings.tab.${tab}`) })}
          onClose={() => setShowReset(false)}
          onFieldChange={xoaLoiKhiGo.onChange}
          onSubmit={resetGroup}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setShowReset(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-danger" type="submit" disabled={save.isPending}>
                {save.isPending ? t('common.processing') : t('settings.reset.submit')}
              </button>
            </>
          }
        >
          <div className="form-alert warn">
            <Icon name="info" size={16} />
            <span>{t('settings.reset.body')}</span>
          </div>
        </Modal>
      ) : null}

      {showSmtpTest ? <SmtpTestModal onClose={() => setShowSmtpTest(false)} /> : null}
    </div>
  );
}

/** FR-114 — tải logo / favicon qua `POST /settings/branding/logo`. */
function LogoRow({
  slot,
  label,
  url,
}: {
  slot: 'logo_light' | 'logo_dark' | 'favicon';
  label: string;
  url: string | null;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('slot', slot);
      form.append('file', file);
      return api.upload<{ url: string }>('/settings/branding/logo', form);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast(t('settings.branding.uploaded'));
    },
    onError: (err) => toastError(err instanceof ApiError ? err.message : t('state.error.title')),
  });

  return (
    <div className="setting-row">
      <div className="grow">
        <b>{label}</b>
        <div className="sub">{url ? url : t('settings.branding.noImage')}</div>
      </div>
      <label className="btn btn-sm" style={{ cursor: 'pointer' }}>
        {upload.isPending ? t('common.processing') : t('settings.branding.upload')}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          aria-label={label}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) upload.mutate(file);
            event.target.value = '';
          }}
        />
      </label>
    </div>
  );
}

/** FR-113 — gửi thư kiểm tra cấu hình SMTP. */
function SmtpTestModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const { toast } = useToast();
  const [to, setTo] = useState('');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const mutation = useMutation({
    mutationFn: () => api.post<{ to: string }>('/settings/smtp/test', { to }),
    onSuccess: (data) => {
      toast(t('settings.smtp.sent', { email: data.to }));
      onClose();
    },
    onError: (err) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('settings.smtp.testTitle')}
      subtitle={t('settings.smtp.testSubtitle')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('settings.smtp.test')}
          </button>
        </>
      }
    >
      {error && error.fields.length === 0 ? (
        <div className="form-alert bad" role="alert">
          <Icon name="warn" size={16} />
          <span>{error.message}</span>
        </div>
      ) : null}

      <Field
        label={t('settings.smtp.testTo')}
        type="email"
        value={to}
        required
        autoFocus
        onChange={(event) => setTo(event.target.value)}
        error={error?.fieldMessage('to')}
      />
    </Modal>
  );
}

/* ── Theo dõi job nền (docs/09 §9.4) ─────────────────────────────────── */

interface SyncJobItem {
  id: string;
  kind: string;
  status: string;
  attempts: number;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  domainName: string | null;
  connectionName: string | null;
}

interface SyncJobsPayload {
  items: SyncJobItem[];
  summary: {
    kind: string;
    lastRunAt: string | null;
    lastStatus: string | null;
    failures24h: number;
    /** OQ-289 — số việc đang chờ trong hàng đợi của loại job này. */
    queuedCount: number;
    /** OQ-289 — job chạy theo yêu cầu (chỉ khi có việc bù), không quét định kỳ. */
    onDemand: boolean;
    intervalMinutes: number | null;
  }[];
}

const JOB_STATUS_CHIP: Record<string, string> = {
  queued: '',
  running: 'info',
  done: 'ok',
  failed: 'bad',
};

/**
 * docs/09 §9.4 — "Job nền chạy đúng chu kỳ, có trang theo dõi trạng thái job
 * cho Admin". Nằm trong tab *Đồng bộ* của `settings.html` (FR-111) chứ không
 * phải một mục sidebar mới: FR-001 liệt kê đóng ba nhóm sidebar. Xem OQ-075.
 */
function SyncJobsCard() {
  const { formatRelative } = useFormat();
  const t = useT();
  const jobsQuery = useQuery({
    queryKey: ['settings', 'sync-jobs'],
    queryFn: () => api.get<SyncJobsPayload>('/settings/sync-jobs'),
    refetchInterval: 30_000,
  });

  return (
    /* Khoảng cách do `.stack` của tab lo (OQ-318) — thẻ này không tự cộng thêm
       `mt-20` nữa, nếu không hai khoảng cách chồng lên nhau. */
    <div className="card" data-od-id="settings-sync-jobs">
      <div className="card-head">
        <div className="grow">
          <h2>{t('settings.jobs.title')}</h2>
          <div className="sub">{t('settings.jobs.sub')}</div>
        </div>
        <button
          className="btn btn-sm btn-ghost"
          type="button"
          onClick={() => void jobsQuery.refetch()}
          disabled={jobsQuery.isFetching}
        >
          {t('common.refresh')}
        </button>
      </div>

      <AsyncSection
        loading={jobsQuery.isLoading}
        error={jobsQuery.error}
        data={jobsQuery.data}
        onRetry={() => void jobsQuery.refetch()}
        skeleton={<SkeletonCard rows={4} />}
      >
        {(payload) => (
          <>
            <div className="card-body">
              <dl className="kv" data-od-id="settings-sync-summary">
                {payload.summary.map((row) => (
                  <Fragment key={row.kind}>
                    <dt>{t(`settings.jobs.kind.${row.kind}`)}</dt>
                    <dd>
                      <span className={`chip ${JOB_STATUS_CHIP[row.lastStatus ?? ''] ?? ''}`}>
                        <span className="dot" />
                        {/*
                          OQ-289 — "Chưa chạy" và "Không có việc" trông giống
                          hệt nhau trên màn hình, nhưng một cái là sự cố còn cái
                          kia là bình thường.

                          `dns_sync` không quét định kỳ: nó chỉ xử lý việc bù
                          khi một thao tác ghi bản ghi DNS thất bại. Chưa chạy
                          lần nào ở đó nghĩa là **chưa lần nào phải bù** — tin
                          tốt, không phải job hỏng.
                        */}
                        {row.lastStatus
                          ? t(`settings.jobs.status.${row.lastStatus}`)
                          : row.onDemand && row.queuedCount === 0
                            ? t('settings.jobs.idle')
                            : t('settings.jobs.never')}
                      </span>{' '}
                      <span className="small muted num">
                        {row.lastRunAt ? formatRelative(row.lastRunAt) : '—'}
                        {row.intervalMinutes
                          ? ` · ${t('settings.jobs.every', { minutes: row.intervalMinutes })}`
                          : ''}
                        {row.queuedCount > 0
                          ? ` · ${t('settings.jobs.queued', { count: row.queuedCount })}`
                          : ''}
                        {row.failures24h > 0
                          ? ` · ${t('settings.jobs.failures', { count: row.failures24h })}`
                          : ''}
                      </span>
                    </dd>
                  </Fragment>
                ))}
              </dl>
            </div>

            <div className="table-wrap">
              <table className="tbl" data-od-id="settings-sync-table">
                <thead>
                  <tr>
                    <th scope="col">{t('settings.jobs.colKind')}</th>
                    <th scope="col">{t('settings.jobs.colTarget')}</th>
                    <th scope="col">{t('settings.jobs.colStatus')}</th>
                    <th scope="col" className="right">
                      {t('settings.jobs.colAttempts')}
                    </th>
                    <th scope="col" className="right">
                      {t('settings.jobs.colCreated')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payload.items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="small muted">
                        {t('settings.jobs.empty')}
                      </td>
                    </tr>
                  ) : (
                    payload.items.map((job) => (
                      <tr key={job.id}>
                        <td className="mono small">{job.kind}</td>
                        <td className="mono small">
                          {job.domainName ?? job.connectionName ?? '—'}
                        </td>
                        <td>
                          <span className={`chip ${JOB_STATUS_CHIP[job.status] ?? ''}`}>
                            <span className="dot" />
                            {t(`settings.jobs.status.${job.status}`)}
                          </span>
                          {job.error ? <span className="small muted"> · {job.error}</span> : null}
                        </td>
                        <td className="right num small">{job.attempts}</td>
                        <td className="right num small">{formatRelative(job.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </AsyncSection>
    </div>
  );
}
